/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* =================================================================================================
 * Robey customisations
 * ================================================================================================= */

/**
 * Close an open quick buy panel when the backdrop behind it is tapped.
 *
 * The theme only wires up the close button. Rather than duplicating its close
 * logic, hand the click straight to that button - it already knows how to fold
 * the panel away and reset the block. Delegated from the document because the
 * backdrop is rendered per product block.
 */
document.addEventListener('click', (evt) => {
  if (!evt.target.classList || !evt.target.classList.contains('qb-bg')) return;
  const expandedBlock = evt.target.closest('.product-block, product-block');
  const closeButton = expandedBlock && expandedBlock.querySelector('.close-detail');
  if (closeButton) closeButton.click();
});

/**
 * Close the quick buy panel once an item has been added from it.
 *
 * This delay is the length of the tick in the button, so it is set by what a
 * customer needs to take it in rather than by anything technical. The two limits
 * around it: the theme resets the button at 2000ms, and a customer who has just
 * added something wants to get on. 1200ms sits between them.
 *
 * It no longer has to race the "added to cart" notice. That notice is held back
 * until the panel closes (see custom-css, body.expanded .added-notification), so
 * the confirmation now runs in two clear steps - the tick here, the notice after -
 * instead of both at once with the notice dimmed behind the backdrop.
 *
 * Only fires for a form inside a quick buy panel, so adding from a normal product
 * page is untouched.
 */
const RBS_QUICKBUY_CLOSE_DELAY = 1200;

document.addEventListener('on:cart:add', (evt) => {
  const panel = evt.target.closest && evt.target.closest('.quickbuy-container');
  if (!panel) return;
  const block = panel.closest('.product-block, product-block');
  const closeButton = block && block.querySelector('.close-detail');
  if (!closeButton) return;
  setTimeout(() => {
    if (block.classList.contains('expanded')) closeButton.click();
  }, RBS_QUICKBUY_CLOSE_DELAY);
});

/**
 * Once a quick buy panel has been opened, keep the sticky add to cart bar away until
 * the customer goes back to the product of the page themselves.
 *
 * That bar belongs to the page's product. Someone who has just been looking at - or
 * buying - a different item in the panel does not need a large button for the item
 * behind it the moment the panel closes; at that point it reads as an invitation to
 * add the same thing again. It applies whether they added something or just
 * dismissed the panel: either way their attention has been elsewhere.
 *
 * Rather than timing it, wait for the moment the theme itself decides the bar does
 * not belong on screen: sticky-atc.js sets sticky-atc--out while the main buy
 * buttons are in view. Seeing that class appear means the customer has scrolled back
 * up to the main product, which is exactly the signal that the bar is welcome again,
 * so we let go and the theme takes over. Keeping that one threshold means there is
 * only one place deciding when the bar belongs on screen.
 *
 * If they never scroll back up, the bar stays away for the rest of the visit.
 *
 * The hiding itself is CSS, on the rbs-sticky-atc-hold class.
 */
let rbsStickyAtcObserver = null;

document.addEventListener('on:quickbuy:before-open', () => {
  const bar = document.querySelector('sticky-atc');
  if (!bar) return;

  // Already out means the main buy buttons are in view, so the customer is at the
  // product of the page anyway. Nothing to hold back, and holding would never be
  // released: the class is there, so it never changes to trigger the observer.
  if (bar.classList.contains('sticky-atc--out')) return;

  document.body.classList.add('rbs-sticky-atc-hold');

  if (rbsStickyAtcObserver) rbsStickyAtcObserver.disconnect();
  rbsStickyAtcObserver = new MutationObserver(() => {
    if (!bar.classList.contains('sticky-atc--out')) return;
    document.body.classList.remove('rbs-sticky-atc-hold');
    rbsStickyAtcObserver.disconnect();
    rbsStickyAtcObserver = null;
  });
  rbsStickyAtcObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
});

/**
 * Bring the selected colour into view in the colour strip on the product page.
 *
 * Sets scrollLeft rather than using scrollIntoView(), which would also scroll the
 * ancestors and make the page jump vertically while it is still loading. The
 * browser clamps scrollLeft by itself, so centring covers the first and last
 * colour too, without a separate branch.
 *
 * Runs again on load: at DOMContentLoaded the swatch images may not be measured
 * yet, which makes the strip look as if it does not scroll.
 */
function rbsCentreActiveColour() {
  document.querySelectorAll('.product-colors-wrapper').forEach((strip) => {
    if (strip.scrollWidth <= strip.clientWidth) return;
    const active = strip.querySelector('.product-colors__color-label.current');
    if (!active) return;
    strip.scrollLeft = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rbsCentreActiveColour);
} else {
  rbsCentreActiveColour();
}
window.addEventListener('load', rbsCentreActiveColour);

// Product cards: load a card's colour images once the pointer enters its colour row.
if (window.matchMedia('(hover: hover)').matches) {
  document.addEventListener('pointerover', (event) => {
    const row = event.target.closest('.product-block .product-colors');
    if (!row || row.dataset.rbsPreloaded) return;
    row.dataset.rbsPreloaded = 'true';
    row.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  });
}

// Product cards: a click on a colour shows its photo, a click on that photo opens the colour.
document.addEventListener('click', (event) => {
  const colour = event.target.closest('.product-block .product-colors__color-label[data-url]');
  if (!colour) return;
  if (event.target.closest('.color-hover-image')) {
    window.location.href = colour.dataset.url;
    return;
  }
  const active = colour.parentElement.querySelector('.product-colors__color-label.active');
  if (active && active !== colour) active.classList.remove('active');
  colour.classList.toggle('active');
});

// Gallery indicator on mobile: 1/n wide (min. 28px), moves with the scroll position.
function rbsGalleryProgress() {
  document.querySelectorAll('media-gallery .rbs-progress').forEach((track) => {
    const slider = track.parentElement.querySelector('.slider');
    const thumb = track.querySelector('.rbs-progress__fill');
    if (!slider || !thumb || track.dataset.rbsBound) return;
    track.dataset.rbsBound = 'true';

    let range = 0;
    let maxScroll = 0;
    const update = () => {
      const share = maxScroll > 0 ? slider.scrollLeft / maxScroll : 0;
      thumb.style.transform = `translateX(${share * range}px)`;
    };
    const measure = () => {
      const width = Math.max(28, (track.clientWidth * slider.clientWidth) / slider.scrollWidth);
      thumb.style.width = `${width}px`;
      range = track.clientWidth - width;
      maxScroll = slider.scrollWidth - slider.clientWidth;
      update();
    };

    const resize = new ResizeObserver(measure);
    resize.observe(slider);
    if (slider.firstElementChild) resize.observe(slider.firstElementChild);
    slider.addEventListener('scroll', update, { passive: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rbsGalleryProgress);
} else {
  rbsGalleryProgress();
}
window.addEventListener('load', rbsGalleryProgress);

// Floating carousel arrows sit halfway down the photos.
function rbsPlaceCarouselNav(carousel) {
  const nav = carousel.querySelector('.collection-slider__nav');
  const image = carousel.querySelector('.slider__item .image-cont');
  if (!nav || !image) return;

  // Relative to the listing, which the fade-in animation moves as a whole.
  const listing = nav.nextElementSibling;
  const inListing = image.getBoundingClientRect().top - listing.getBoundingClientRect().top;
  const top = listing.offsetTop - nav.offsetTop + inListing + image.offsetHeight / 2;
  nav.style.setProperty('--collection-slider-nav-top', `${top}px`);
}

const rbsMeasuredCarousels = new WeakSet();

function rbsWatchCarousels() {
  document.querySelectorAll('.collection-slider--floating-nav').forEach((carousel) => {
    const listing = carousel.querySelector('.collection-listing');
    if (!listing || rbsMeasuredCarousels.has(carousel)) return;
    rbsMeasuredCarousels.add(carousel);
    new ResizeObserver(() => rbsPlaceCarouselNav(carousel)).observe(listing);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rbsWatchCarousels);
} else {
  rbsWatchCarousels();
}

let rbsWatchQueued = false;
new MutationObserver(() => {
  if (rbsWatchQueued) return;
  rbsWatchQueued = true;
  requestAnimationFrame(() => {
    rbsWatchQueued = false;
    rbsWatchCarousels();
  });
}).observe(document.documentElement, { childList: true, subtree: true });
